import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Button,
  Badge,
  Dropdown,
  Option,
  Tooltip,
  Switch,
  tokens,
} from '@fluentui/react-components';
import {
  FilterRegular,
  ArrowClockwiseRegular,
  EyeRegular,
  MailRegular,
  DocumentBulletListRegular,
} from '@fluentui/react-icons';
import DataTable, { type Column } from '../components/DataTable';
import { TableSkeleton } from '../components/LoadingSkeleton';
import {
  getIrsNotices,
  getIrsNoticeStats,
  getSystemUsers,
  type IrsNoticeFilters,
} from '../services/api';
import {
  type IrsNotice,
  type IrsNoticeStatus,
  type SystemUser,
  irsNoticeStatusDisplayNames,
  irsNoticeStatusColors,
  irsNoticeStatusOptions,
} from '../types';
import {
  daysUntilDue,
  dueBucket,
  formatDueLabel,
  formatNoticeDate,
} from '../utils/irsNoticeDue';
import NoticeDetailPanel from '../components/NoticeDetailPanel';
import { useIrsNoticesScopeParams } from './useIrsNoticesScopeParams';

/**
 * The IRS letter / notice queue.
 *
 * The feature exists to pass one test: a client calls with a letter in their hand and the staff
 * member who picks up answers — what letter, when it arrived, when it is due, who owns it, where we
 * are, and did we already amend — without hanging up and without opening a second system. This page
 * is how they FIND the notice; `NoticeDetailPanel` is where they answer the question.
 *
 * ⚠ There is deliberately no "New notice" button here. A notice is a fact *about a return*, and the
 * create endpoint is `POST /tax-returns/{id}/irs-notices` — the server derives clientId, taxYear and
 * contactId from that parent return and ignores anything a caller sends. Creating from a queue would
 * mean asking the user to pick a return, which is the return detail page's job.
 */

/** Row accent by urgency. Mirrors the server's stat buckets; `none` is grey, never red. */
const dueBucketColors: Record<string, string> = {
  overdue: '#EF4444',
  due_soon: '#F59E0B',
  later: '#10B981',
  none: '#9CA3AF',
};

const currentYear = new Date().getFullYear();
const taxYearOptions = [currentYear, currentYear - 1, currentYear - 2, currentYear - 3];

export default function IrsNoticesPage() {
  const queryClient = useQueryClient();
  const {
    status: selectedStatus,
    assignee: selectedAssignee,
    taxYear: selectedTaxYear,
    openOnly,
    setStatus: setSelectedStatus,
    setAssignee: setSelectedAssignee,
    setTaxYear: setSelectedTaxYear,
    setOpenOnly,
    clearAll: clearScopeParams,
  } = useIrsNoticesScopeParams();
  const [selectedNoticeId, setSelectedNoticeId] = useState<string | null>(null);
  const [selectedClientId, setSelectedClientId] = useState<string | null>(null);
  const [detailPanelOpen, setDetailPanelOpen] = useState(false);

  // One reference instant per render, so every row in a single paint is measured against the same
  // "now" — two rows computed microseconds apart must not straddle a date boundary.
  const now = new Date();

  const filters: IrsNoticeFilters = {
    status: selectedStatus,
    assignedTo: selectedAssignee,
    taxYear: selectedTaxYear,
    openOnly: openOnly || undefined,
  };

  const { data: notices, isLoading, error, refetch } = useQuery<IrsNotice[]>({
    queryKey: ['irsNotices', filters],
    queryFn: () => getIrsNotices(filters),
    refetchInterval: 60000,
  });

  const { data: stats } = useQuery({
    queryKey: ['irsNoticeStats'],
    queryFn: getIrsNoticeStats,
    refetchInterval: 60000,
  });

  const { data: systemUsers } = useQuery<SystemUser[]>({
    queryKey: ['systemUsers'],
    queryFn: getSystemUsers,
  });

  const clearFilters = clearScopeParams;

  const handleViewNotice = (notice: IrsNotice) => {
    // The panel refetches the notice by (id, clientId) rather than receiving the row object: the
    // detail response carries amendment statuses resolved LIVE from the linked returns, which the
    // list row does not have and must not pretend to.
    setSelectedNoticeId(notice.id);
    setSelectedClientId(notice.clientId);
    setDetailPanelOpen(true);
  };

  const invalidateNotices = () => {
    queryClient.invalidateQueries({ queryKey: ['irsNotices'] });
    queryClient.invalidateQueries({ queryKey: ['irsNoticeStats'] });
  };

  const columns: Column<IrsNotice>[] = [
    {
      key: 'urgency',
      header: '',
      width: '40px',
      render: (notice) => {
        const bucket = dueBucket(daysUntilDue(notice.responseDueDate, now));
        return (
          <Tooltip content={formatDueLabel(notice.responseDueDate, now)} relationship="label">
            <div
              style={{
                width: '8px',
                height: '8px',
                borderRadius: '50%',
                backgroundColor: dueBucketColors[bucket],
              }}
            />
          </Tooltip>
        );
      },
    },
    {
      key: 'noticeNumber',
      header: 'Notice',
      sortable: true,
      render: (notice) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <MailRegular />
          <div>
            <div style={{ fontWeight: 500 }}>{notice.noticeNumber || 'Unnumbered'}</div>
            {notice.authority !== 'irs' && (
              <div style={{ fontSize: tokens.fontSizeBase200, color: 'var(--tl-text-secondary)', textTransform: 'uppercase' }}>
                {notice.authority}
              </div>
            )}
          </div>
        </div>
      ),
    },
    {
      key: 'clientName',
      header: 'Client',
      sortable: true,
      render: (notice) => (
        <div>
          <div style={{ fontWeight: 500 }}>{notice.clientName || '-'}</div>
          {/* On a business return clientName is the company and contactName is a person there —
              different guids, measured 40/40. Show both when they differ; showing only one would
              hide who to actually ask for on the phone. */}
          {notice.contactName && notice.contactName !== notice.clientName && (
            <div style={{ fontSize: tokens.fontSizeBase200, color: 'var(--tl-text-secondary)' }}>
              {notice.contactName}
            </div>
          )}
        </div>
      ),
    },
    {
      key: 'taxYear',
      header: 'Tax Year',
      sortable: true,
      width: '90px',
    },
    {
      key: 'noticeReceivedDate',
      header: 'Received',
      sortable: true,
      getValue: (notice) =>
        notice.noticeReceivedDate ? new Date(notice.noticeReceivedDate).getTime() : null,
      // UTC day, not local — see formatNoticeDate. A local render would show every US reader the
      // day before, and would disagree with the UTC-based day counts in the Response Due column.
      render: (notice) => formatNoticeDate(notice.noticeReceivedDate, '-'),
    },
    {
      key: 'responseDueDate',
      header: 'Response Due',
      sortable: true,
      // null, NOT 0 or Infinity, when there is no due date. The table comparator handles null
      // explicitly and sorts it last ascending — so a notice with no deadline never heads an
      // urgency-sorted queue, and never reads as though it were due at the epoch.
      getValue: (notice) =>
        notice.responseDueDate ? new Date(notice.responseDueDate).getTime() : null,
      render: (notice) => {
        const days = daysUntilDue(notice.responseDueDate, now);
        const bucket = dueBucket(days);
        const label = formatDueLabel(notice.responseDueDate, now);
        return (
          <div>
            <div
              style={{
                fontWeight: bucket === 'overdue' ? 600 : 400,
                color: bucket === 'overdue' ? dueBucketColors.overdue : undefined,
              }}
            >
              {label}
            </div>
            {notice.responseDueDate && days !== null && (
              <div style={{ fontSize: tokens.fontSizeBase200, color: 'var(--tl-text-secondary)' }}>
                {formatNoticeDate(notice.responseDueDate)}
              </div>
            )}
          </div>
        );
      },
    },
    {
      key: 'status',
      header: 'Status',
      sortable: true,
      render: (notice) => (
        <Badge
          appearance="filled"
          style={{ backgroundColor: irsNoticeStatusColors[notice.status], color: 'white' }}
        >
          {irsNoticeStatusDisplayNames[notice.status] || notice.status}
        </Badge>
      ),
    },
    {
      key: 'assignedToName',
      header: 'Assigned To',
      sortable: true,
      render: (notice) =>
        notice.assignedToName ? (
          <span>{notice.assignedToName}</span>
        ) : (
          <span style={{ color: 'var(--tl-text-secondary)', fontStyle: 'italic' }}>Unassigned</span>
        ),
    },
    {
      key: 'amendments',
      header: 'Amendments',
      width: '110px',
      getValue: (notice) => notice.amendmentLinks.length,
      render: (notice) =>
        notice.amendmentLinks.length > 0 ? (
          <Tooltip
            content={notice.amendmentLinks.map((l) => `${l.taxYear} amendment`).join(', ')}
            relationship="label"
          >
            <Badge appearance="tint" color="informative" icon={<DocumentBulletListRegular />}>
              {notice.amendmentLinks.length}
            </Badge>
          </Tooltip>
        ) : (
          <span style={{ color: 'var(--tl-text-secondary)' }}>—</span>
        ),
    },
    {
      key: 'actions',
      header: '',
      width: '60px',
      render: (notice) => (
        <Tooltip content="Open notice" relationship="label">
          <Button
            appearance="subtle"
            icon={<EyeRegular />}
            size="small"
            onClick={() => handleViewNotice(notice)}
          />
        </Tooltip>
      ),
    },
  ];

  if (isLoading) {
    return <TableSkeleton />;
  }

  if (error) {
    return (
      <div className="error-container">
        <p>Failed to load IRS notices. Please try again.</p>
        <Button onClick={() => refetch()}>Retry</Button>
      </div>
    );
  }

  return (
    <div>
      <div className="page-header">
        <h2>IRS Letters &amp; Notices</h2>
        <p>Every notice, who owns it, and what it is waiting on — answer the call from one screen</p>
      </div>

      {stats && (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
            gap: '16px',
            marginBottom: '24px',
          }}
        >
          <div className="card" style={{ padding: '16px', textAlign: 'center' }}>
            <div style={{ fontSize: tokens.fontSizeHero700, fontWeight: 600, color: '#0078d4' }}>{stats.open}</div>
            <div style={{ fontSize: '13px', color: 'var(--tl-text-secondary)' }}>Open</div>
          </div>
          <div className="card" style={{ padding: '16px', textAlign: 'center' }}>
            <div style={{ fontSize: tokens.fontSizeHero700, fontWeight: 600, color: '#EF4444' }}>{stats.overdue}</div>
            <div style={{ fontSize: '13px', color: 'var(--tl-text-secondary)' }}>Overdue</div>
          </div>
          <div className="card" style={{ padding: '16px', textAlign: 'center' }}>
            <div style={{ fontSize: tokens.fontSizeHero700, fontWeight: 600, color: '#F59E0B' }}>{stats.dueSoon}</div>
            <div style={{ fontSize: '13px', color: 'var(--tl-text-secondary)' }}>Due in 14 Days</div>
          </div>
          <div className="card" style={{ padding: '16px', textAlign: 'center' }}>
            <div style={{ fontSize: tokens.fontSizeHero700, fontWeight: 600, color: '#8B5CF6' }}>
              {stats.unassigned}
            </div>
            <div style={{ fontSize: '13px', color: 'var(--tl-text-secondary)' }}>Unassigned</div>
          </div>
          <div className="card" style={{ padding: '16px', textAlign: 'center' }}>
            <div style={{ fontSize: tokens.fontSizeHero700, fontWeight: 600 }}>{stats.total}</div>
            <div style={{ fontSize: '13px', color: 'var(--tl-text-secondary)' }}>All Notices</div>
          </div>
        </div>
      )}

      <div className="toolbar">
        <div className="filter-group">
          <FilterRegular />
          <Dropdown
            placeholder="Status"
            value={selectedStatus ? irsNoticeStatusDisplayNames[selectedStatus] : ''}
            onOptionSelect={(_, data) =>
              setSelectedStatus((data.optionValue as IrsNoticeStatus) || undefined)
            }
            style={{ minWidth: '200px' }}
          >
            <Option text="All Statuses" value="">
              All Statuses
            </Option>
            {irsNoticeStatusOptions.map((opt) => (
              <Option key={opt.value} text={opt.label} value={opt.value}>
                {opt.label}
              </Option>
            ))}
          </Dropdown>

          <Dropdown
            placeholder="Assigned To"
            value={
              selectedAssignee
                ? (systemUsers?.find((u) => u.systemUserId === selectedAssignee)?.fullName ?? '')
                : ''
            }
            onOptionSelect={(_, data) => setSelectedAssignee(data.optionValue || undefined)}
            style={{ minWidth: '180px' }}
          >
            <Option text="Anyone" value="">
              Anyone
            </Option>
            {/* Disabled accounts are excluded: a departed staff member is not a valid new owner.
                Notices ALREADY assigned to one still render their stored assignedToName, so the
                filter never hides existing ownership — it only stops new assignment. */}
            {(systemUsers ?? [])
              .filter((user) => !user.isDisabled)
              .map((user) => (
                <Option key={user.systemUserId} text={user.fullName} value={user.systemUserId}>
                  {user.fullName}
                </Option>
              ))}
          </Dropdown>

          <Dropdown
            placeholder="Tax Year"
            value={selectedTaxYear ? String(selectedTaxYear) : ''}
            onOptionSelect={(_, data) =>
              setSelectedTaxYear(data.optionValue ? Number(data.optionValue) : undefined)
            }
            style={{ minWidth: '130px' }}
          >
            <Option text="All Years" value="">
              All Years
            </Option>
            {taxYearOptions.map((year) => (
              <Option key={year} text={String(year)} value={String(year)}>
                {String(year)}
              </Option>
            ))}
          </Dropdown>

          <Switch
            checked={openOnly}
            onChange={(_, data) => setOpenOnly(data.checked)}
            label="Open only"
          />
        </div>

        <Button appearance="subtle" icon={<FilterRegular />} onClick={clearFilters}>
          Clear Filters
        </Button>

        <Button appearance="subtle" icon={<ArrowClockwiseRegular />} onClick={() => refetch()}>
          Refresh
        </Button>
      </div>

      <div className="card">
        <DataTable
          data={notices || []}
          columns={columns}
          rowKey="id"
          searchable
          searchPlaceholder="Search by notice number, client, or contact..."
          searchKeys={['noticeNumber', 'clientName', 'contactName']}
          onRowClick={handleViewNotice}
          emptyMessage={
            openOnly
              ? 'No open notices. Switch off "Open only" to include resolved and closed notices.'
              : 'No notices found.'
          }
          paginated
          // Ascending: the most urgent work leads. Notices with no recorded deadline sort last
          // rather than first, because the table comparator puts nulls at the end ascending.
          initialSort={{ column: 'responseDueDate', direction: 'asc' }}
        />
      </div>

      {selectedNoticeId && selectedClientId && (
        <NoticeDetailPanel
          noticeId={selectedNoticeId}
          clientId={selectedClientId}
          open={detailPanelOpen}
          onOpenChange={setDetailPanelOpen}
          systemUsers={systemUsers}
          onUpdate={invalidateNotices}
        />
      )}
    </div>
  );
}
